import {
  EngagementListsWithUser,
  EngagementMedia,
  EngagementMediaType,
  EngagementPlatformType,
  EngagementProductListWithUser,
  MediaResponseDto,
  ProductListResponseDto,
  ProductListResponseDtoPageResponseDto,
  ProductListType,
} from '@epicuro-next/data/engagement/engagement-models';
import { IUser } from '@epicuro-next/data/users';
import {
  ListType,
  MediaPart,
  MediaType,
} from '@epicuro-next/models/engagement';
// tslint:disable-next-line:nx-enforce-module-boundaries
import { UserEntity } from '@epicuro-next/state/user-entities';
import { isNonNullish, isNullish } from '@epicuro-next/utilities';
import { decode } from 'html-entities';

// This is a mapping file and we need any on some of type castings
// tslint:disable:no-any

export function mapEngagementSystemToLegacy(
  resp: ProductListResponseDtoPageResponseDto,
  userEntities: UserEntity[],
): EngagementListsWithUser {
  return {
    total: resp.total,
    items: (resp.items ?? []).map((item) => {
      return mapEngagementListToLegacy(item, userEntities);
    }),
  };
}

// When delete mapping - move decode logic to other place (map in service response)
export function mapEngagementListToLegacy(
  engagementList: ProductListResponseDto,
  userEntities: UserEntity[],
): EngagementProductListWithUser {
  const ownerId = engagementList.createdByUserId;
  const userEntity = userEntities.find(
    (e) => e.userExternalId === ownerId,
  )?.user;
  const owner = mapEngagementListOwner(userEntity);
  const engagementOriginalProductList = engagementList.originalProductList;

  const originalProductList = engagementOriginalProductList
    ? {
        id: engagementOriginalProductList.id,
        userExternalId: engagementOriginalProductList.createdByUserId,
      }
    : undefined;

  return {
    ...engagementList,
    name: decode(engagementList.name),
    description: decode(engagementList.description),
    creation: {
      dateUtc: engagementList.createdAtUtc?.toString() ?? '',
      user: owner as any,
    },
    update: {
      dateUtc: engagementList.updatedAtUtc?.toString() ?? '',
      user: owner as any,
    },
    mainMedia: isNonNullish(engagementList.mainMedia)
      ? mapEngagementMediaToLegacy(engagementList.mainMedia)
      : null,
    type: mapEngagementListTypeToLegacy(engagementList.type) as any,
    newsfeedId: engagementList.newsfeedId?.toString() as any,
    originalProductList: originalProductList as any,
    availableProductsCount: engagementList.availableProductsCount ?? 0,
    unavailableProductsCount: engagementList.unavailableProductsCount ?? 0,
    amount:
      (engagementList.availableProductsCount ?? 0) +
      (engagementList.unavailableProductsCount ?? 0),
  };
}

export function mapEngagementListOwner(user?: IUser) {
  if (isNullish(user)) return user;

  const { imageLink: profilePictureUrl, ...userInfo } = user;

  return { ...userInfo, profilePictureUrl };
}

export function mapEngagementListTypeToLegacy(
  listType?: ProductListType,
): ListType {
  if (isNullish(listType)) return ListType.Normal;

  if (listType === ProductListType.Power) return ListType.Power;
  if (listType === ProductListType.Commercial) return ListType.Commercial;

  return ListType.Normal;
}

export function mapEngagementMediaToLegacy(
  media: MediaResponseDto,
): EngagementMedia {
  return {
    ...media,
    type: undefined,
    mediaType: mapEngagementMediaTypeToLegacy(media?.type, media?.platform),
    mediaPart: MediaPart.MainImage as any,
  };
}

export function mapEngagementMediaTypeToLegacy(
  mediaType?: EngagementMediaType,
  platform?: EngagementPlatformType,
): MediaType {
  const mapVideoWithPlatform = () => {
    switch (platform) {
      case EngagementPlatformType.Vimeo:
        return MediaType.Vimeo;
      case EngagementPlatformType.Youtube:
        return MediaType.Youtube;
      case EngagementPlatformType.Native:
      default:
        return MediaType.Video;
    }
  };

  switch (mediaType) {
    case EngagementMediaType.Video:
      return mapVideoWithPlatform();
    case EngagementMediaType.DirectShow:
      return MediaType.Directshow;
    case EngagementMediaType.Livestream:
      return MediaType.Livestream;
    case EngagementMediaType.Image:
    default:
      return MediaType.Image;
  }
}
